/**
 * Google / Apple / GitHub のボタン（2026-09-27 creator/dev-oauth-ui）。/login・/signup（intent=login）と、
 * 再認証のダイアログ（components/Reauth.tsx・intent=reauth）・ログイン方法の追加（pages/AccountLoginMethods.tsx・intent=link）で使う。
 *
 * - 出すのは GET /v1/platform/info の oauthProviders にあるものだけ（devOAuthClient.ts の loadOAuthProviders）。
 *   ChatGPT（窓口が招待制）・Claude（規約で禁止）のボタンは作らない（サーバーの providers.ts が返さない）。
 * - 見た目は各社のブランドの決まりに合わせる（公開資料を読んだ範囲）:
 *   Google = 白地・灰色の枠・4 色の「G」・「Google で続ける / Continue with Google」
 *   Apple  = 黒地・白のロゴと文字・「Apple で続ける / Continue with Apple」（ロゴと文字の色を揃える・ロゴだけを変形しない）
 *   GitHub = 濃い灰色の地・白の Octocat の印・「GitHub で続ける」
 *   ロゴは各社の公開の形をそのまま（色・比率を変えない）。文字の大きさは 3 つで揃える（どれかを目立たせない）
 * - 2026-09-28 creator/login-redesign: 並び（外部が上）・前回使用の印・パスキーの置き場を足した（OAuthButtons の説明）
 */
import { startTransition, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import type { ApiError } from '../api';
import { PROVIDER_NAMES, type DevOAuthProvider } from '../devOAuth';
import { signupInviteError, type InviteInput, type SignupInviteRequirement } from '../inviteCode';
import { loadLoginOptions, loadOAuthProviders, startDevOAuth, type StartOptions } from '../devOAuthClient';
import { errorText, useT } from '../i18n';
import { isLaunchHidden } from '../launchHidden';
import { deviceStorage, orderProviders, recordLogin, type LoginMethod } from '../lastLogin';
import { NO_LOGIN_OPTIONS, passkeyLoginError, showPasskeyLogin, type LoginOptions } from '../loginOptions';
import { passkeySignIn } from '../loginPasskeyClient';
import { passkeysSupported } from '../passkey';
import { viaDecision } from '../signupVia';
import { useSession } from '../session';

export function ProviderIcon({ provider }: { provider: DevOAuthProvider }) {
  if (provider === 'google') {
    return (
      <svg viewBox="0 0 48 48" width="18" height="18" aria-hidden="true" className="oauth-icon">
        <path fill="#EA4335" d="M24 9.5c3.54 0 6.71 1.22 9.21 3.6l6.85-6.85C35.9 2.38 30.47 0 24 0 14.62 0 6.51 5.38 2.56 13.22l7.98 6.19C12.43 13.72 17.74 9.5 24 9.5z" />
        <path fill="#4285F4" d="M46.98 24.55c0-1.57-.15-3.09-.38-4.55H24v9.02h12.94c-.58 2.96-2.26 5.48-4.78 7.18l7.73 6c4.51-4.18 7.09-10.36 7.09-17.65z" />
        <path fill="#FBBC05" d="M10.53 28.59c-.48-1.45-.76-2.99-.76-4.59s.27-3.14.76-4.59l-7.98-6.19C.92 16.46 0 20.12 0 24c0 3.88.92 7.54 2.56 10.78l7.97-6.19z" />
        <path fill="#34A853" d="M24 48c6.48 0 11.93-2.13 15.89-5.81l-7.73-6c-2.15 1.45-4.92 2.3-8.16 2.3-6.26 0-11.57-4.22-13.47-9.91l-7.98 6.19C6.51 42.62 14.62 48 24 48z" />
      </svg>
    );
  }
  if (provider === 'apple') {
    return (
      <svg viewBox="0 0 24 24" width="18" height="18" aria-hidden="true" className="oauth-icon" fill="currentColor">
        <path d="M16.365 1.43c0 1.14-.493 2.27-1.177 3.08-.744.9-1.99 1.57-2.987 1.57-.12 0-.23-.02-.3-.03-.01-.06-.04-.22-.04-.39 0-1.15.572-2.27 1.206-2.98.804-.94 2.142-1.64 3.248-1.68.03.13.05.28.05.43zm4.565 15.71c-.03.07-.463 1.58-1.518 3.12-.945 1.34-1.94 2.71-3.43 2.71-1.517 0-1.9-.88-3.63-.88-1.698 0-2.302.91-3.67.91-1.377 0-2.332-1.26-3.428-2.8-1.287-1.82-2.323-4.63-2.323-7.28 0-4.28 2.797-6.55 5.552-6.55 1.448 0 2.675.95 3.6.95.865 0 2.222-1.01 3.902-1.01.613 0 2.886.06 4.374 2.19-.13.09-2.383 1.37-2.383 4.19 0 3.26 2.854 4.42 2.955 4.45z" />
      </svg>
    );
  }
  return (
    <svg viewBox="0 0 16 16" width="18" height="18" aria-hidden="true" className="oauth-icon" fill="currentColor">
      <path d="M8 0C3.58 0 0 3.58 0 8c0 3.54 2.29 6.53 5.47 7.59.4.07.55-.17.55-.38 0-.19-.01-.82-.01-1.49-2.01.37-2.53-.49-2.69-.94-.09-.23-.48-.94-.82-1.13-.28-.15-.68-.52-.01-.53.63-.01 1.08.58 1.23.82.72 1.21 1.87.87 2.33.66.07-.52.28-.87.51-1.07-1.78-.2-3.64-.89-3.64-3.95 0-.87.31-1.59.82-2.15-.08-.2-.36-1.02.08-2.12 0 0 .67-.21 2.2.82.64-.18 1.32-.27 2-.27.68 0 1.36.09 2 .27 1.53-1.04 2.2-.82 2.2-.82.44 1.1.16 1.92.08 2.12.51.56.82 1.27.82 2.15 0 3.07-1.87 3.75-3.65 3.95.29.25.54.73.54 1.48 0 1.07-.01 1.93-.01 2.2 0 .21.15.46.55.38A8.013 8.013 0 0016 8c0-4.42-3.58-8-8-8z" />
    </svg>
  );
}

/**
 * 使えるプロバイダ。読み込み中は null・失敗は空（2026-10-22 site/early-inline-signup: /early からの自動の開始 = signupVia.ts が
 * 「まだ読めていない」と「無い」を分けるため。以前は読み込み中も空だった = 下の useOAuthProviders がその形のまま）
 */
export function useOAuthProviderList(): DevOAuthProvider[] | null {
  const [list, setList] = useState<DevOAuthProvider[] | null>(null);
  useEffect(() => {
    let alive = true;
    void loadOAuthProviders().then((l) => alive && setList(l));
    return () => {
      alive = false;
    };
  }, []);
  return list;
}

/** 使えるプロバイダ（読み込み中・失敗は空 = ボタンを出さない） */
export function useOAuthProviders(): DevOAuthProvider[] {
  return useOAuthProviderList() ?? [];
}

/** ログイン画面に要る /v1/platform/info の値（パスキーの印を含む）。読み込み中・失敗は「何も無い」 */
export function useLoginOptions(): LoginOptions {
  const [o, setO] = useState<LoginOptions>(NO_LOGIN_OPTIONS);
  useEffect(() => {
    let alive = true;
    void loadLoginOptions().then((v) => alive && setO(v));
    return () => {
      alive = false;
    };
  }, []);
  return o;
}

/**
 * 「前回使用 / Last used」の小さな印（2026-09-28 creator/login-redesign）。ボタンの右上の枠に重ねる（styles.css の .last-used）。
 * WHY 枠に重ねるか: ボタンの中に並べると、375px 幅で中央寄せの文字とぶつかる。枠の上なら文字の位置を動かさない
 */
export function LastUsedBadge() {
  const { t } = useT();
  return <span className="last-used">{t('auth.lastUsed')}</span>;
}

/** 1 つのプロバイダのボタン。label は画面ごと（「で続ける」「で確認」「を追加」）。lastUsed = 前回この方法でログインした */
export function ProviderButton({
  provider,
  label,
  onClick,
  disabled,
  lastUsed
}: {
  provider: DevOAuthProvider;
  label: string;
  onClick: () => void;
  disabled?: boolean;
  lastUsed?: boolean;
}) {
  return (
    // type="button": ログイン・登録のフォームの中に置くので、送信（パスワードでのログイン）と混ざらないように
    <button type="button" className={`btn btn-block oauth-btn oauth-${provider}`} onClick={onClick} disabled={disabled}>
      <ProviderIcon provider={provider} />
      <span>{label}</span>
      {lastUsed && <LastUsedBadge />}
    </button>
  );
}

/** パスキーの印（鍵）。インラインの SVG（CSP で外から画像を読まない） */
export function PasskeyIcon() {
  return (
    <svg viewBox="0 0 24 24" width="18" height="18" aria-hidden="true" className="oauth-icon" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
      <circle cx="8" cy="8" r="4" />
      <path d="M2.5 20v-1.5A4.5 4.5 0 0 1 7 14h2.5" />
      <circle cx="17" cy="12.5" r="2.5" />
      <path d="M17 15v6l1.5-1.2L17 18.6" />
    </svg>
  );
}

/**
 * プロバイダの画面へ移る操作（押したら start → 移る）。失敗は error に文言を入れる。
 * busy の間はほかのボタンも押させない（二重に往復を始めると、先の控えが後の控えで上書きされて戻りが合わなくなる）
 */
export function useStartOAuth() {
  const [busy, setBusy] = useState<DevOAuthProvider | null>(null);
  const [error, setError] = useState<string | null>(null);
  const start = async (o: StartOptions) => {
    setBusy(o.provider);
    setError(null);
    try {
      await startDevOAuth(o);
      // 成功 = ページが移る。戻る（bfcache）で帰ってきたときに押せるよう、少し後に busy を解く
      setTimeout(() => setBusy(null), 3000);
    } catch (err) {
      // provider_not_configured・redirect_not_allowed・reauthentication_* は i18n の `error.<code>`（errorText が引く）
      // redirect_not_allowed は既存の文言（プレイヤーの戻り先 URL の話）と意味が違う = このダッシュボードのオリジンが KUMO_DASHBOARD_URL と違う
      setError(errorText(err as ApiError, { redirect_not_allowed: 'oauth.error.redirectNotAllowed' }));
      setBusy(null);
      throw err;
    }
  };
  return { start, busy, error, setError };
}

/**
 * /login・/signup の外部のログインの並び（2026-09-28 creator/login-redesign で並びを変えた）。
 *   - 並び: Vercel / Cloudflare にならい、外部のボタンを上に・区切り（「または」）の下にメールとパスワード。
 *     前回メールで入った人（ログイン画面）は、メールの欄を上に・このボタンの並びを下にする（divider="above"）
 *   - 前回使ったプロバイダを先頭へ + 「前回使用」の印（lastUsed = lastLogin.ts が覚えている方法）
 *   - パスキーのボタン: サーバーの passkeyLogin が true・ブラウザが WebAuthn を持つ・passkey（ログイン画面だけ）のとき（loginOptions.ts の showPasskeyLogin）。
 *     2026-09-28 creator/passkey-login でつないだ: 押す → loginPasskeyClient.ts の passkeySignIn → パスワードのログインと同じく navigate + signIn、
 *     この端末の「前回使用」に passkey を記録（lastLogin.ts。「保存しない」の端末では書かない）。
 *     登録画面（/signup）では出さない: パスキーだけでは新規登録できない（確認済みの既存アカウントに /account で足す = Core の案）。
 *     押せるのは既存のアカウントだけなので、登録に来た人に「ログイン」のボタンを見せると迷わせる
 * returnTo = ログインの後に行く画面（afterLoginPath で確かめた値）。ボタンが 1 つも無ければ区切りごと出さない
 * invite = 登録の画面の招待コードの欄の値（2026-09-29 creator/launch-money・inviteCode.ts）。ok なら控えに入れて戻りの exchange に付ける。
 *   形が違えば押しても往復を始めない（プロバイダの画面へ行って戻ってから断られると往復が丸ごと無駄）= その場で文言を出す。
 *   招待制（inviteRequirement = required・2026-10-06 core/invite-only）で空のときも同じく止める
 * autoStart = /early の「Continue with GitHub / Google」から来た（/signup?via=…・2026-10-22 site/early-inline-signup・signupVia.ts）。
 *   一覧が読めたら、そのボタンを押した時と同じ処理を 1 回だけ走らせる（招待の検査も同じ）。始めなかった・失敗したら onAutoStartEnd(false)
 *   = 登録の画面がふつうの形に戻る（エラーはこのボタンの下の 1 行）。移れたら onAutoStartEnd(true)（ページが移るので画面はそのまま）
 */
export function OAuthButtons({
  returnTo,
  last = null,
  divider = 'below',
  passkey: allowPasskey = false,
  invite,
  inviteRequirement = 'unknown',
  fromAd = false,
  src = null,
  autoStart = null,
  onAutoStartEnd
}: {
  returnTo: string;
  last?: LoginMethod | null;
  divider?: 'below' | 'above';
  /** パスキーでログインのボタンを置くか（ログイン画面だけ true） */
  passkey?: boolean;
  /** 登録の画面の招待コード（ログイン画面では渡さない） */
  invite?: InviteInput;
  /** 招待制か（登録の画面だけ渡す・2026-10-06 core/invite-only）。required で欄が空なら押しても往復を始めない */
  inviteRequirement?: SignupInviteRequirement;
  /** 広告から来た登録か（登録の画面だけ・2026-10-18 inviteCode.ts の adSourceFromSearch）。控えに入れ、戻りで断られたときの文を選ぶ */
  fromAd?: boolean;
  /** 登録の画面の URL の src（2026-10-19 ops/usage-origin §3・inviteCode.ts の adSourceFromSearch）。控えに入れ、戻りの exchange に付ける */
  src?: string | null;
  /** /early から来たときのプロバイダ（登録の画面だけ・signupVia.ts の viaFromSearch） */
  autoStart?: DevOAuthProvider | null;
  /** 自動の開始が終わった（true = プロバイダへ移る・false = 始めなかった / 失敗） */
  onAutoStartEnd?: (started: boolean) => void;
}) {
  const { t } = useT();
  const navigate = useNavigate();
  const { signIn } = useSession();
  const loaded = useOAuthProviderList();
  const providers = orderProviders(loaded ?? [], last);
  const options = useLoginOptions();
  const { start, busy, error, setError } = useStartOAuth();
  const [passkeyBusy, setPasskeyBusy] = useState(false);
  const [passkeyError, setPasskeyError] = useState<string | null>(null);
  const [autoDone, setAutoDone] = useState(false);
  const passkey = allowPasskey && showPasskeyLogin(options, passkeysSupported());

  /** 1 つのプロバイダで往復を始める（ボタンを押した時と /early からの自動の開始の両方）。始めなければ false */
  const begin = (p: DevOAuthProvider): Promise<boolean> => {
    // 登録の画面だけ（ログインの画面は invite を渡さない = 今どおり）。招待制で空・形の違いは往復を始めずにその場で出す
    //   WHY: 戻ってから 400 invite_code_required で断られると往復が丸ごと無駄（一回限りのコードも消費される）
    const inviteError = invite ? signupInviteError(inviteRequirement, invite) : null;
    if (inviteError) {
      setError(t(`error.${inviteError}`));
      return Promise.resolve(false);
    }
    return start({ provider: p, intent: 'login', returnTo, ...(invite?.kind === 'ok' ? { inviteCode: invite.code } : {}), ...(fromAd ? { fromAd: true } : {}), ...(src ? { src } : {}) }).then(
      () => true,
      () => false
    );
  };

  // /early からの自動の開始（上の autoStart）。判断は signupVia.ts の viaDecision（一覧が読めるまで待つ・1 回だけ）
  useEffect(() => {
    if (!autoStart || autoDone) return;
    const d = viaDecision({ via: autoStart, providers: loaded, invite: invite ?? { kind: 'none' }, requirement: inviteRequirement });
    if (d.kind === 'wait') return;
    setAutoDone(true);
    if (d.kind === 'skip') {
      if (d.inviteError) setError(t(`error.${d.inviteError}`));
      onAutoStartEnd?.(false);
      return;
    }
    void begin(d.provider).then((ok) => onAutoStartEnd?.(ok));
    // begin・t・invite は描くたびに作り直される。autoDone で 1 回に絞るので、読めた一覧と旗だけを見る
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [autoStart, autoDone, loaded, inviteRequirement]);

  if (!providers.length && !passkey) return null;
  const sep = (
    <div className="auth-divider" role="separator">
      <span>{t('oauth.or')}</span>
    </div>
  );

  const signInWithPasskey = async () => {
    setPasskeyError(null);
    setPasskeyBusy(true);
    try {
      const r = await passkeySignIn();
      recordLogin(deviceStorage(), 'passkey');
      // パスワードのログイン（AuthPage の LoginPage）と同じ: navigate と signIn を同じ transition にまとめる（履歴を二重にしない）
      startTransition(() => {
        navigate(returnTo);
        signIn(r.token, r.developer);
      });
    } catch (err) {
      const kind = passkeyLoginError(err);
      // 取り消し（プロンプトを閉じた）は何も出さない = 本人の選択。ほかは一般の文言（どのパスキーが・なぜ、は出さない = サーバーと同じ方針）
      // 出金を閉じている間は「出金用のパスキー」を言わない（2026-09-30 console/words-pass）
      if (kind === 'failed') setPasskeyError(t(isLaunchHidden('payouts') ? 'auth.passkeyFailedLaunch' : 'auth.passkeyFailed'));
      else if (kind === 'unavailable') setPasskeyError(t('auth.passkeyUnavailable'));
      else if (kind === 'other') setPasskeyError(errorText(err as ApiError));
      setPasskeyBusy(false);
    }
  };

  const passkeyButton = passkey && (
    <button key="passkey" type="button" className="btn btn-block oauth-btn oauth-passkey" disabled={!!busy || passkeyBusy} onClick={() => void signInWithPasskey()}>
      <PasskeyIcon />
      <span>{passkeyBusy ? t('auth.busy') : t('auth.passkeyLogin')}</span>
      {last === 'passkey' && !passkeyBusy && <LastUsedBadge />}
    </button>
  );
  return (
    <div className="oauth-block">
      {divider === 'above' && sep}
      <div className="oauth-list">
        {last === 'passkey' && passkeyButton}
        {providers.map((p) => (
          <ProviderButton
            key={p}
            provider={p}
            label={busy === p ? t('auth.busy') : t('oauth.continueWith', { provider: PROVIDER_NAMES[p] })}
            disabled={!!busy || passkeyBusy}
            lastUsed={last === p}
            onClick={() => void begin(p)}
          />
        ))}
        {last !== 'passkey' && passkeyButton}
      </div>
      {(error || passkeyError) && (
        <p className="alert alert-error" role="alert">
          {error ?? passkeyError}
        </p>
      )}
      {divider === 'below' && sep}
    </div>
  );
}
